import { ValueGetterParams } from "ag-grid-community";
import { DateFormat, now } from "common";
import { omit } from "lodash";
import React from "react";
import { CatalogTermType, FieldType, Guest, Product, Report } from "types";
import { MembershipPurchaseItem } from "types/src/types/MembershipPurchaseItem";
import { ProductRenderer } from "../ColumnTypes";
import { CustomMembershipProductFilter } from "../CustomFilters/CustomProductFilter";
import { CustomReport } from "../CustomReport";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";
import { useResource } from "@/hooks/useResource";

export const MembershipsReport = ({ report }: { report: Report }) => {
    const columns = React.useMemo(() => getColumnDefs(report), [report]);

    return <CustomReport dateFields={["createdAt"]} report={report} columns={columns} unwind={unwind} />;
};

interface MembershipPurchaseItemRow extends Omit<MembershipPurchaseItem, "guests"> {
    guest: Guest;
}

const unwind = (data: MembershipPurchaseItem[]) => {
    const unwoundData = [];

    for (const item of data) {
        if (Array.isArray(item.guests)) {
            if (item.formResponse) {
                item.guests[0].formResponse = item.formResponse;
            }

            for (const guest of item.guests) {
                if (guest.membershipCode) {
                    unwoundData.push({ ...omit(item, ["guests", "formResponse"]), guest });
                }
            }
        }
    }

    return unwoundData;
};

const getColumnDefs = (report: Report): ReportColumnDef[] => [
    {
        field: "id",
        headerName: "Reservation ID",
        type: ["objectid", "purchase"],
    },
    {
        field: "createdAt",
        headerName: "Purchase Date",
        type: "datetime",
        lockVisible: false,
        minWidth: 200,
        valueGetter: (params: ValueGetterParams) => params.data.purchase.createdAt,
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "guest.membershipCode.code",
        headerName: "Code",
        maxWidth: 200,
        cellClass: "no-translate",
        valueFormatter: ({ value }) => value.toUpperCase(),
    },
    {
        field: "product",
        headerName: "Product",
        type: "product",
        filter: CustomMembershipProductFilter,
        filterParams: { report },
        minWidth: 200,
        cellRenderer: ProductRenderer,
    },
    {
        field: "guest.membershipCode.uses",
        headerName: "Uses",
        filter: false,
        maxWidth: 150,
        type: "number",
    },
    {
        field: "guest.membershipCode.limit",
        headerName: "Usage Limit",
        filter: false,
        maxWidth: 150,
        type: "number",
        cellRenderer: CodeUsageLimit,
    },
    {
        field: "guest.membershipCode.expiry",
        headerName: "Expiry",
        filter: false,
        maxWidth: 200,
        type: "date",
        cellRenderer: CodeExpiry,
    },
    {
        field: "guest.name",
        headerName: "Member Name",
        minWidth: 200,
    },
    {
        field: "guest.email",
        headerName: "Member Email",
        valueGetter: getMemberEmailInfo,
        minWidth: 200,
    },
];

const getMemberEmailInfo = ({ data }: { data: MembershipPurchaseItemRow }) => {
    const fields = data.guest.formResponse?.fields ?? [];
    const emailField = fields.find((field) => field.type === FieldType.EMAIL);

    if (emailField) {
        return emailField.value;
    }

    return data.guest.primary ? data.organizer.email ?? "" : "";
};

const CodeExpiry = ({ data }: { data: MembershipPurchaseItemRow }) => {
    const id = data?.product.id;
    const { data: product } = useResource<Product>(["products", id], null, {
        enabled: !!id,
    });

    if (!product?.catalog?.terms?.length) {
        return null;
    }

    const expiryTerm = product.catalog.terms.find((term) => term.type === CatalogTermType.EXPIRY);
    if (!expiryTerm?.value) {
        return null;
    }

    const expiryInDays = expiryTerm.value;
    return now(data.createdAt).add(expiryInDays, "days").format(DateFormat.DATE);
};

const CodeUsageLimit = ({ data }: { data: MembershipPurchaseItemRow }) => {
    const id = data?.product.id;

    const { data: product } = useResource<Product>(["products", id], null, {
        enabled: !!id,
    });

    if (!product) {
        return "";
    }

    if (!product.catalog?.terms?.length) {
        return "Unlimited";
    }

    const usageLimitTerm = product.catalog.terms.find((term) => term.type === CatalogTermType.USAGE_LIMIT);
    return usageLimitTerm ? usageLimitTerm.value : "Unlimited";
};
